/*
 * Orbit's route recommendation, as a pure function.
 *
 * The Platform UI Guide is explicit that risk and solution size are separate
 * questions: sensitive retrieval can stay a Quick Win with stronger controls,
 * and a small-looking request can need a Project because it crosses a new
 * consequence boundary. So this does not score an answer sheet. It checks the
 * conditions the guide names, and every condition that fires returns the
 * sentence it fired for.
 *
 * The reasons are the product, not a debug aid. A recommendation a person
 * cannot argue with is a recommendation they cannot correct.
 */

export type Audience = 'me' | 'team' | 'teams' | 'company';
export type Frequency = 'once' | 'repeated' | 'scheduled' | 'continuous';
export type InputKind = 'personal' | 'context' | 'app' | 'restricted';
export type ActionKind = 'read' | 'draft' | 'recommend' | 'act';
export type Impact = 'easy' | 'costly' | 'material' | 'regulated';

export interface RoutingAnswers {
  audience: Audience;
  frequency: Frequency;
  input: InputKind;
  action: ActionKind;
  impact: Impact;
}

export type Route = 'quick_win' | 'project';

/* What Orbit recommends we BUILD. `route` answers how big the work is; this
   answers what shape the thing is. Neither is the delivered kind, which is read
   after the fact from whether a published skill links back to the request. */
export type Deliverable = 'skill' | 'plugin' | 'document' | 'routine';

/* The registry's fixed capability vocabulary, as the backend declares it in
   routes/aai/home.py. The wizard already asks what the work DOES, so the tag
   is read off that answer rather than left for somebody to fill in later. */
export type Capability = 'summarization' | 'drafting' | 'analysis' | 'automation' | 'other';

export interface Recommendation {
  route: Route;
  /** Why it landed here. Always at least one line. */
  reasons: string[];
  /** The controls that follow from the answers, whichever route it took. */
  controls: string[];
  /** Maps to aai_request.risk. */
  risk: 'low' | 'medium' | 'high';
  /** Maps to aai_request.capability_tag. */
  capability: Capability;
  /** Maps to aai_request.deliverable. */
  deliverable: Deliverable;
  /** Why it is that shape. One line, in the same voice as the route reasons. */
  deliverableReason: string;
}

const PROJECT_RULES: Array<{ when: (a: RoutingAnswers) => boolean; reason: string }> = [
  {
    when: (a) => a.audience === 'company' || a.audience === 'teams',
    reason: 'Several teams or the whole company are affected, so it needs one accountable owner.',
  },
  {
    when: (a) => a.impact === 'material' || a.impact === 'regulated',
    reason: 'The consequence is material or regulated, so it needs independent approval and a rollback path.',
  },
  {
    when: (a) => a.action === 'act' && a.impact !== 'easy',
    reason: 'It acts inside another app where a mistake is not easy to correct, so it needs a human gate.',
  },
  {
    when: (a) => a.frequency === 'continuous',
    reason: 'It runs continuously, which means durable state somebody has to operate.',
  },
];

const CONTROL_RULES: Array<{ when: (a: RoutingAnswers) => boolean; control: string }> = [
  { when: (a) => a.input === 'restricted', control: 'Restricted data: source rights are checked separately from your role.' },
  { when: (a) => a.input === 'app', control: 'Connected app: it reads through your own approved connection, never a shared one.' },
  { when: (a) => a.action === 'act', control: 'Acts in an app: every action stops at an explicit confirmation.' },
  { when: (a) => a.action === 'draft' || a.action === 'recommend', control: 'Drafts and recommendations: you review the result before anything leaves.' },
  { when: (a) => a.frequency !== 'once', control: 'Repeats: it carries a version, an owner, and a review date.' },
  { when: (a) => a.audience !== 'me', control: 'Shared with others: the audience is approved before anyone else can run it.' },
];

/* The shape rules, from Josh's framing on the call. First match wins, because
   these are not additive the way the project rules are: a thing is one shape.
   The order is the order the conditions actually override each other. */
const DELIVERABLE_RULES: Array<{
  when: (a: RoutingAnswers) => boolean;
  deliverable: Deliverable;
  reason: string;
}> = [
  {
    when: (a) => a.input === 'app' && a.action === 'act',
    deliverable: 'plugin',
    reason: 'It acts inside a connected app, so the connection and the steps have to be held together as one thing.',
  },
  {
    when: (a) => a.frequency === 'scheduled' || a.frequency === 'continuous',
    deliverable: 'routine',
    reason: 'It runs on a clock rather than when somebody asks, so it is a routine with an owner and a review date.',
  },
  {
    when: (a) => a.frequency === 'once',
    deliverable: 'document',
    reason: 'It is asked for once, so what you get is the piece of work itself, not something to run again.',
  },
];

const DEFAULT_DELIVERABLE_REASON =
  'It is the same task done again and again, which is what a skill is for.';

const RISK: Record<Impact, Recommendation['risk']> = {
  easy: 'low',
  costly: 'medium',
  material: 'high',
  regulated: 'high',
};

const CAPABILITY: Record<ActionKind, Capability> = {
  read: 'summarization',
  draft: 'drafting',
  recommend: 'analysis',
  act: 'automation',
};

export function recommendRoute(answers: RoutingAnswers): Recommendation {
  const reasons = PROJECT_RULES.filter((r) => r.when(answers)).map((r) => r.reason);
  const controls = CONTROL_RULES.filter((r) => r.when(answers)).map((r) => r.control);
  const risk = RISK[answers.impact];
  const capability = CAPABILITY[answers.action];

  let deliverable: Deliverable = 'skill';
  let deliverableReason = DEFAULT_DELIVERABLE_REASON;
  const shape = DELIVERABLE_RULES.find((r) => r.when(answers));
  if (shape) {
    deliverable = shape.deliverable;
    deliverableReason = shape.reason;
  }

  if (reasons.length > 0) {
    return { route: 'project', reasons, controls, risk, capability, deliverable, deliverableReason };
  }
  return {
    route: 'quick_win',
    reasons: ['It is one bounded result inside an approved boundary, so it does not need coordinated delivery.'],
    controls,
    risk,
    capability,
    deliverable,
    deliverableReason,
  };
}

export const ROUTE_LABEL: Record<Route, string> = {
  quick_win: 'Quick Win',
  project: 'Project',
};

export const ROUTE_MEANING: Record<Route, string> = {
  quick_win: 'One focused result, delivered now or through a reusable setup you own.',
  project: 'Coordinated work with a named owner, shared delivery, and a release decision.',
};

export const DELIVERABLE_LABEL: Record<Deliverable, string> = {
  skill: 'Skill',
  plugin: 'Plugin',
  document: 'Document',
  routine: 'Routine',
};

export const DELIVERABLE_MEANING: Record<Deliverable, string> = {
  skill: 'One repeated task, saved so anyone allowed can run it again.',
  plugin: 'Several steps across several apps, with their connections, held together as one thing.',
  document: 'The piece of work itself, produced once.',
  routine: 'Work that runs on a schedule, with an owner watching it.',
};
